import { useEffect, useRef, useState } from "react";

const stats = [
  { value: "100+", label: "Clients Served" },
  { value: "15", label: "Years in Practice" },
  { value: "98%", label: "Retention Rate" },
];

const highlights = [
  "QuickBooks Certified — setup, training & cleanup",
  "Sales tax specialists — including Florida & multi-state compliance",
  "Serving small businesses, LLCs, freelancers & individuals nationwide",
  "Dedicated accountant — no call centers, no bots",
  "Transparent flat-rate pricing, no surprises",
  "100% remote-friendly — work with us from anywhere",
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="bg-white py-10 md:py-24 lg:py-28 px-4 md:px-6">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Image */}
        <div
          className={`transition-all duration-700 ${
            visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
          }`}
        >
          <div className="relative mx-auto max-w-xs sm:max-w-sm lg:max-w-none">
            <div className="w-full aspect-[4/3] sm:aspect-[4/4] lg:aspect-[4/5] rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/f31cf9f5e1762f64254d796b2f20c052.jpg"
                alt="Professional Accounting Team"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Floating badges */}
            <div className="absolute -bottom-3 -right-2 md:-bottom-5 md:-right-5 bg-white rounded-lg px-4 md:px-6 py-2.5 md:py-5 border border-charcoal-100">
              <p className="text-forest-700 font-cormorant text-xl md:text-3xl font-semibold">15+</p>
              <p className="text-charcoal-500 text-xs font-medium mt-0.5">Years of Trust</p>
            </div>
            <div className="absolute -top-2 -left-2 md:-top-4 md:-left-4 bg-forest-600 rounded-lg px-3 md:px-4 py-1.5 md:py-3">
              <p className="text-white text-xs font-semibold whitespace-nowrap">QuickBooks Certified</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className={`transition-all duration-700 delay-200 pt-6 lg:pt-0 ${
            visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
          }`}
        >
          <div className="flex items-center gap-3 mb-3 md:mb-5">
            <div className="w-8 h-0.5 bg-forest-500" />
            <span className="text-forest-600 text-xs font-semibold tracking-[0.25em] uppercase">
              About Us
            </span>
          </div>
          <h2 className="font-cormorant text-2xl md:text-4xl lg:text-5xl font-semibold text-charcoal-900 mb-3 md:mb-6 leading-tight">
            Your Trusted Financial Partner, Wherever You Are
          </h2>
          <p className="text-charcoal-500 text-xs md:text-base leading-relaxed mb-3 md:mb-5">
            We provide professional accounting, bookkeeping, and tax services for
            small businesses, LLCs, freelancers, and individuals — no matter where
            you&apos;re located. Our practice is fully remote-friendly and built to
            serve clients across the United States.
          </p>
          <p className="text-charcoal-500 text-xs md:text-base leading-relaxed mb-5 md:mb-8">
            We specialize in Florida sales tax compliance (one of the most complex
            state tax environments), QuickBooks setup and cleanup, catch-up
            bookkeeping, and ongoing monthly financial management. Whether your
            books are a mess or you just need a reliable partner — we&apos;ve got you.
          </p>

          {/* Highlights */}
          <ul className="space-y-2 md:space-y-3 mb-6 md:mb-10">
            {highlights.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-charcoal-600 text-xs md:text-sm">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-check-line text-forest-600 font-bold text-sm md:text-base" />
                </div>
                {item}
              </li>
            ))}
          </ul>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 pt-5 md:pt-8 border-t border-charcoal-100">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="font-cormorant text-xl md:text-3xl font-semibold text-forest-700">{stat.value}</p>
                <p className="text-charcoal-500 text-xs font-medium mt-0.5 md:mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
